import { AxiosError, AxiosResponse } from 'axios';
import { FormikErrors, FormikValues } from 'formik';
import { set } from 'lodash';
import { Renderable, toast } from 'react-hot-toast';
import { matchPath } from 'react-router-dom';
import { publicPages } from '@/modules/common/routes.tsx';

export interface CompliOneApiError {
  error_code: number;
  error_detail: string;
  error_message: string;
  validation_errors: ValidationErrorDetailItem[];
}

export interface ValidationErrorDetailItem {
  loc: string[];
  msg: string;
  type: string;
}

export function formatApiErrorMessage(error: AxiosError<CompliOneApiError>): string {
  return error.response?.data?.error_message || error.message || 'Unknown error';
}

export function extractValidationErrors(
  error: AxiosError<CompliOneApiError>,
): FormikErrors<FormikValues> {
  if (error.response?.status === 400 && error.response?.data?.validation_errors) {
    return error.response?.data?.validation_errors.reduce((acc, item) => {
      const key = item.loc.filter(node => node !== 'body').join('.');
      set(acc, key, item.msg);
      return acc;
    }, {} as FormikErrors<FormikValues>);
  }
  return {};
}

export async function withErrorHandling<T>(
  fn: Promise<AxiosResponse<T>>,
  params: {
    loading: string | Renderable;
    success: string | Renderable;
    setErrors?: (errors: FormikErrors<FormikValues>) => void;
    notificationDuration?: number;
    customErrorMessage?: string;
  } = {
    loading: 'Loading...',
    success: 'Success!',
    notificationDuration: 2500,
    customErrorMessage: undefined,
  },
): Promise<AxiosResponse<T>> {
  const toastId = toast.loading(params.loading);
  try {
    const response = await fn;

    if (params.success) {
      toast.success(params.success, {
        id: toastId,
        duration: params.notificationDuration ?? 2500,
      });
    } else {
      toast.dismiss(toastId);
    }

    return response;
  } catch (e) {
    const error = e as AxiosError<CompliOneApiError>;

    let errorMessage: Renderable = <div>{formatApiErrorMessage(error)}</div>;

    if (error.response?.status === 400 && params?.customErrorMessage) {
      errorMessage = <div>{params?.customErrorMessage}</div>;
    }
    if (
      error.response?.status === 400 &&
      error.response?.data?.validation_errors &&
      params.setErrors
    ) {
      params.setErrors?.(extractValidationErrors(error));
      errorMessage = <div>Validation error occurred, check your input data.</div>;
    }
    const isPublicAccessPage = publicPages.some(path => matchPath(path, location.pathname));
    if (error.response?.status === 418 || (isPublicAccessPage && error.response?.status === 401)) {
      toast.dismiss(toastId);
    } else {
      toast.error(errorMessage, {
        id: toastId,
        duration: params.notificationDuration,
      });
    }
    throw e;
  }
}
